import classNames from 'classnames';
import { forwardRef, PropsWithChildren, ReactNode } from 'react';
import { useIntl } from 'react-intl';
import Button from '@alltrails/denali/components/Button';
import IconButton from '@alltrails/denali/components/IconButton';
import ArrowSq from '@alltrails/denali/icons/ArrowSq';
import Chevron from '@alltrails/denali/icons/Chevron';
import styles from './SidebarTitleArea.module.scss';

type SidebarTitleAreaProps = {
  backLabel?: ReactNode;
  canCollapse: boolean;
  collapsed: boolean;
  hideOverflow?: boolean;
  onBackRequest?: () => void;
  onCollapseRequest?: () => void;
  additionalButton?: React.ReactElement;
  hideBackButton?: boolean;
};

const SidebarTitleArea = forwardRef<HTMLDivElement, PropsWithChildren<SidebarTitleAreaProps>>(
  (
    {
      backLabel,
      canCollapse,
      children,
      collapsed,
      onBackRequest,
      onCollapseRequest,
      hideOverflow,
      additionalButton,
      hideBackButton
    }: PropsWithChildren<SidebarTitleAreaProps>,
    ref
  ) => {
    const intl = useIntl();
    const showBackButton = !!onBackRequest && !hideBackButton;
    return (
      <div className={styles.container} ref={ref}>
        <div className={styles.titleRow}>
          {showBackButton ? (
            backLabel ? (
              <Button icon={{ Component: ArrowSq, orientation: 'left' }} onClick={onBackRequest} size="sm" text={backLabel} variant="secondary" />
            ) : (
              <IconButton
                icon={{ Component: ArrowSq, orientation: 'left' }}
                onClick={onBackRequest}
                size="sm"
                title={intl.formatMessage({ defaultMessage: 'Go back' })}
              />
            )
          ) : (
            <div
              className={classNames(styles.title, {
                [styles.collapsed]: collapsed && !hideOverflow,
                [styles.hideOverflow]: hideOverflow && !collapsed
              })}
            >
              {children}
            </div>
          )}
          {(additionalButton || canCollapse) && (
            <div className={styles.buttonsContainer}>
              {additionalButton}
              {canCollapse && (
                <IconButton
                  className={styles.collapseButton}
                  icon={{ Component: Chevron, orientation: collapsed ? 'down' : 'up' }}
                  onClick={onCollapseRequest}
                  size="sm"
                  title={
                    collapsed ? intl.formatMessage({ defaultMessage: 'Expand panel' }) : intl.formatMessage({ defaultMessage: 'Collapse panel' })
                  }
                />
              )}
            </div>
          )}
        </div>
        {showBackButton && children && <div className={classNames(styles.title, { [styles.collapsed]: collapsed })}>{children}</div>}
      </div>
    );
  }
);

export default SidebarTitleArea;
